/**
 * API del asistente de migración Frontmy → clínica Coofit (Panel → /migracion-frontmy).
 */

import { httpClient } from '../http/client/httpClient';

export type ReportKind = 'pacientes' | 'caja' | 'agenda' | 'gestion';
export const REPORT_KINDS: ReportKind[] = ['pacientes', 'caja', 'agenda', 'gestion'];

export interface FileIssue {
  reporte: ReportKind;
  archivo: string | null;
  codigo: string;
  pareceSer?: ReportKind;
  detalle?: string;
}

export interface AnalyzedFile {
  nombre: string;
  filas: number;
  desde: string | null;
  hasta: string | null;
}

export interface FrontmyProfesional {
  clave: string;
  nombre: string;
  turnos: number;
  sugeridoId: number | null;
}

export interface SaldoAConfirmar {
  ficha: string;
  paciente: string;
  saldoCalculado: number;
  incidencias: string[];
}

export interface MigrationAnalysis {
  archivos: Record<ReportKind, AnalyzedFile[]>;
  errores: FileIssue[];
  clinica: {
    pacientes: number;
    reutilizables: number;
    consultorios: ConsultorioInfo[];
    lotes: LoteInfo[];
  } | null;
  profesionales: FrontmyProfesional[];
  profesionalesClinica: Array<{ id: number; nombre: string }>;
  saldosAConfirmar: SaldoAConfirmar[];
  previa: {
    pacientes: number;
    personas: number;
    conDni: number;
    turnos: number;
    movimientos: number;
    nombresDuplicados: number;
    incidenciasSaldo: number;
  } | null;
}

export interface SummaryItem {
  nivel: 'ok' | 'aviso' | 'info';
  codigo: string;
  cantidad: number;
  total?: number;
  motivos?: Array<{ codigo: string; cantidad: number }>;
  archivo?: string;
}

export interface PendingTurno {
  motivo: string;
  paciente: string;
  fecha: string;
  hora: string;
  servicio: string;
  archivo: string;
  fila: number | string;
}

export interface RunSummary {
  modo: 'prueba' | 'aplicar';
  terminado: string;
  items: SummaryItem[];
  turnosPendientes: PendingTurno[];
  turnosPendientesTotal: number;
}

export interface RunParams {
  migrarContacto: boolean;
  forzar: boolean;
  profesionales: Record<string, number>;
  saldos: Array<{ ficha: string; saldo: string; motivo: string }>;
  consultorioId: number | null;
}

export interface ConsultorioInfo {
  id: number;
  nombre: string;
  turnos: number;
  movimientos: number;
}

/** Una migración aplicada en la clínica (registrada para poder deshacerla). */
export interface LoteInfo {
  id: number;
  guid: string;
  consultorioId: number;
  consultorioNombre: string | null;
  fecha: string;
  usuario: string | null;
  estado: 'APLICADA' | 'DESHECHA';
  deshechaEn: string | null;
  deshechaPor: string | null;
  esUltima: boolean;
}

export interface UndoBlock {
  codigo: string;
  cantidad: number;
  tabla?: string;
}

export interface UndoPreview {
  lote: LoteInfo;
  conteos: Record<string, number>;
  bloqueos: UndoBlock[];
}

export interface ClinicState {
  tenantId: string;
  tenantNombre: string;
  consultorios: ConsultorioInfo[];
  lotes: LoteInfo[];
}

export type JobStatus = 'analizado' | 'ejecutando' | 'probado' | 'aplicado' | 'error';

export interface MigrationJob {
  id: string;
  tenantId: string;
  tenantNombre: string;
  creado: string;
  creadoPor: string | null;
  estado: JobStatus;
  analisis: MigrationAnalysis;
  ejecutando: { modo: 'prueba' | 'aplicar'; desde: string } | null;
  ultimaPrueba: { hash: string; resumen: RunSummary; params: RunParams } | null;
  ultimosParams?: RunParams | null;
  aplicacion: { resumen: RunSummary; deshecha?: { en: string; por: string | null } } | null;
  error: { modo: 'prueba' | 'aplicar'; mensaje: string } | null;
  lineas: string[];
}

/** Error con el `codigo` que manda la API, para traducirlo en pantalla. */
export class MigracionApiError extends Error {
  constructor(readonly codigo: string | null, message: string) {
    super(message);
  }
}

function toError(err: unknown): MigracionApiError {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { codigo?: string; message?: string } } }).response?.data;
    if (data?.codigo || data?.message) return new MigracionApiError(data.codigo ?? null, data.message ?? '');
  }
  return new MigracionApiError(null, err instanceof Error ? err.message : String(err));
}

/** Subir y leer varios Excel grandes puede tardar bastante más que el timeout por defecto. */
const LONG_TIMEOUT = 10 * 60 * 1000;

const ANALYSIS_POLL_MS = 2000;
/** El análisis más largo visto anda por los minutos; pasado esto lo damos por colgado. */
const ANALYSIS_MAX_MS = 30 * 60 * 1000;
/** Cortes de red seguidos que toleramos mientras consultamos (ej. un deploy reiniciando la API). */
const ANALYSIS_MAX_NETWORK_FAILS = 5;

type AnalysisStatus = { estado: 'analizando' } | { estado: 'listo'; job: MigrationJob };

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * Sube los archivos y espera el resultado. La API responde enseguida con un id y
 * analiza en segundo plano (con clínicas grandes tarda más que el timeout del proxy),
 * así que acá consultamos hasta que termina.
 */
export async function analyzeMigration(tenantId: string, files: Record<ReportKind, File[]>): Promise<MigrationJob> {
  const form = new FormData();
  form.append('tenantId', tenantId);
  for (const kind of REPORT_KINDS) for (const file of files[kind]) form.append(kind, file, file.name);
  let id: string;
  try {
    const res = await httpClient.post<{ id: string }>('/migracion-frontmy/analisis', form, {
      timeout: LONG_TIMEOUT,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    id = res.data.id;
  } catch (err) {
    throw toError(err);
  }

  const deadline = Date.now() + ANALYSIS_MAX_MS;
  let networkFails = 0;
  while (Date.now() < deadline) {
    await sleep(ANALYSIS_POLL_MS);
    try {
      const res = await httpClient.get<AnalysisStatus>(`/migracion-frontmy/analisis/${id}`);
      networkFails = 0;
      if (res.data.estado === 'listo') return res.data.job;
    } catch (err) {
      const response = (err as { response?: { status?: number; data?: { codigo?: string } } }).response;
      // Un error de la API (análisis fallido, interrumpido…) es definitivo; un corte de red
      // o un 5xx del proxy sin código (API reiniciando), no.
      const transient = !response || (!response.data?.codigo && (response.status ?? 0) >= 500);
      if (!transient || ++networkFails > ANALYSIS_MAX_NETWORK_FAILS) throw toError(err);
    }
  }
  throw new MigracionApiError('ANALISIS_SIN_RESPUESTA', '');
}

export async function getMigration(id: string): Promise<MigrationJob> {
  try {
    return (await httpClient.get<MigrationJob>(`/migracion-frontmy/${id}`)).data;
  } catch (err) {
    throw toError(err);
  }
}

export async function startMigration(id: string, modo: 'prueba' | 'aplicar', params: RunParams): Promise<MigrationJob> {
  try {
    return (await httpClient.post<MigrationJob>(`/migracion-frontmy/${id}/${modo}`, params)).data;
  } catch (err) {
    throw toError(err);
  }
}

export async function getClinicState(tenantId: string): Promise<ClinicState> {
  try {
    return (await httpClient.get<ClinicState>(`/migracion-frontmy/clinica/${tenantId}`)).data;
  } catch (err) {
    throw toError(err);
  }
}

export async function getUndoPreview(tenantId: string, guid: string): Promise<UndoPreview> {
  try {
    return (await httpClient.get<UndoPreview>(`/migracion-frontmy/clinica/${tenantId}/lotes/${guid}/deshacer`, {
      timeout: LONG_TIMEOUT,
    })).data;
  } catch (err) {
    throw toError(err);
  }
}

/** Devuelve los bloqueos si no se pudo deshacer (nada se borró en ese caso). */
export async function undoMigration(
  tenantId: string,
  guid: string,
  confirmacion: string,
): Promise<{ ok: true; conteos: Record<string, number> } | { ok: false; bloqueos: UndoBlock[] }> {
  try {
    const res = await httpClient.post<{ ok: true; conteos: Record<string, number> }>(
      `/migracion-frontmy/clinica/${tenantId}/lotes/${guid}/deshacer`,
      { confirmacion },
      { timeout: LONG_TIMEOUT },
    );
    return res.data;
  } catch (err) {
    const data = (err as { response?: { status?: number; data?: { codigo?: string; bloqueos?: UndoBlock[] } } }).response;
    if (data?.status === 409 && data.data?.codigo === 'DESHACER_BLOQUEADO') return { ok: false, bloqueos: data.data.bloqueos ?? [] };
    throw toError(err);
  }
}

export async function downloadMigrationLogs(id: string): Promise<void> {
  try {
    const res = await httpClient.get<Blob>(`/migracion-frontmy/${id}/logs.zip`, {
      responseType: 'blob',
      timeout: LONG_TIMEOUT,
    });
    const disposition = String(res.headers['content-disposition'] ?? '');
    const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `migracion-${id.slice(0, 8)}.zip`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    throw toError(err);
  }
}
